import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SortingState, createColumnHelper } from "@tanstack/react-table";
import TableComponent from "../../../../shared/components/table";
import { ARROW_LEFT, CLOUD_UPLOAD, EXPORT_WHITE } from "../../../../assets";
import { Search } from "lucide-react";

const columnHelper = createColumnHelper<any>();

const VIEW_CAUSE_LIST_DATA = [
  {
    id: 1,
    sittingDate: "Mon, 3rd May 2024",
    caseTitle: "Olugbenga Akintola vs Israel Anyemalu",
    appealNumber: "CV/3047/2018",
    classification: "Civil",
    judge: "Hon. Justice. E Agha",
    court: "Court 8",
    status: "Mention",
    adjournment: "June 8, 2024",
    virtualLink: "https://docs.google.com/s",
  },
  {
    id: 2,
    sittingDate: "Mon, 3rd May 2024",
    caseTitle: "Olugbenga Akintola vs Israel Anyemalu",
    appealNumber: "CV/3047/2018",
    classification: "Land",
    judge: "Hon. Justice. E Agha",
    court: "Court 8",
    status: "Hearing",
    adjournment: "June 8, 2024",
    virtualLink: "https://docs.google.com/s",
  },
  {
    id: 3,
    sittingDate: "Mon, 3rd May 2024",
    caseTitle: "Olugbenga Akintola vs Israel Anyemalu",
    appealNumber: "CV/3047/2018",
    classification: "Matrimonial",
    judge: "Hon. Justice. E Agha",
    court: "Court 8",
    status: "Trial",
    adjournment: "June 8, 2024",
    virtualLink: "https://docs.google.com/s",
  },
  {
    id: 4,
    sittingDate: "Mon, 3rd May 2024",
    caseTitle: "Olugbenga Akintola vs Israel Anyemalu",
    appealNumber: "CV/3047/2018",
    classification: "Matrimonial",
    judge: "Hon. Justice. E Agha",
    court: "Court 8",
    status: "Ruling",
    adjournment: "June 8, 2024",
    virtualLink: "https://docs.google.com/s",
  },
  {
    id: 5,
    sittingDate: "Mon, 3rd May 2024",
    caseTitle: "Olugbenga Akintola vs Israel Anyemalu",
    appealNumber: "CV/3047/2018",
    classification: "Civil",
    judge: "Hon. Justice. E Agha",
    court: "Court 8",
    status: "Judgement",
    adjournment: "June 8, 2024",
    virtualLink: "https://docs.google.com/s",
  },
];

const viewCauseListColumns = [
  columnHelper.accessor("sn", {
    header: "S/n",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("sittingDate", {
    header: "Sitting date",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("caseTitle", {
    header: "Case title",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("appealNumber", {
    header: "Appeal number",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("classification", {
    header: "Classification",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("judge", {
    header: "Judge",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("court", {
    header: "Court",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("adjournment", {
    header: "Adjournment",
    cell: (info) => (
      <span className="text-[#111827] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("virtualLink", {
    header: "Virtual link",
    cell: (info) => (
      <a
        href={info.getValue()}
        target="_blank"
        rel="noreferrer"
        className="text-[#2563EB] text-sm underline"
      >
        {info.getValue()}
      </a>
    ),
  }),
];

function ViewCauseList() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const causeList = state?.causeList;

  const titleDate = causeList?.date || "May 3, 2024";
  const causeListName =
    causeList?.name || "Hon. Justice Michael Ojo's Cause list (Week 20)";

  const tableData = useMemo(
    () =>
      VIEW_CAUSE_LIST_DATA.map((item, index) => ({
        ...item,
        sn: index + 1,
      })),
    [],
  );

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return tableData;
    const term = searchTerm.toLowerCase();
    return tableData.filter((item) => {
      return (
        item.sittingDate.toLowerCase().includes(term) ||
        item.caseTitle.toLowerCase().includes(term) ||
        item.appealNumber.toLowerCase().includes(term) ||
        item.classification.toLowerCase().includes(term) ||
        item.judge.toLowerCase().includes(term) ||
        item.court.toLowerCase().includes(term) ||
        item.status.toLowerCase().includes(term) ||
        item.adjournment.toLowerCase().includes(term)
      );
    });
  }, [searchTerm, tableData]);

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="bg-white">
        <div className="px-8 pt-6 pb-4 border-b flex items-center justify-between w-full border-[#E9EAEB]">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">
            Cause list{" "}
            <span className="text-[24px] font-semibold text-[#4B5563] ml-2">
              {titleDate}
            </span>
          </h1>
        </div>
        <div className="px-8 py-3 border-b border-[#E9EAEB] flex items-center justify-between">
          <div className="flex items-center gap-x-2 text-sm">
            <button
              type="button"
              onClick={() => navigate("/admin/overview")}
              className="text-[#98A2B3] hover:text-[#00B074]"
            >
              Home
            </button>
            <img src={ARROW_LEFT} alt="" className="w-[14px] h-[14px]" />
            <button
              type="button"
              onClick={() => navigate("/admin/case-folders")}
              className="text-[#98A2B3] hover:text-[#00B074]"
            >
              Case folder
            </button>
            <img src={ARROW_LEFT} alt="" className="w-[14px] h-[14px]" />
            <span className="font-medium text-[#1A1A1A]">{causeListName}</span>
          </div>

          <button
            type="button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00B074] text-white text-sm font-semibold shadow-sm"
          >
            <img
              src={EXPORT_WHITE}
              className="w-[16px] h-[16px]"
              alt="Export"
            />
            <span>Export</span>
          </button>
        </div>
      </div>

      <div className="flex-1 px-8 py-6">
        <div className="mb-4 max-w-[320px]">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#D0D5DD] focus:outline-none focus:border-[#00B074] bg-white text-sm placeholder:text-[#98A2B3]"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#98A2B3]" />
          </div>
        </div>

        <div className="bg-white ">
          <TableComponent
            DATA={filteredData}
            // @ts-ignore
            COLUMNS={viewCauseListColumns}
            sorting={sorting}
            setSorting={setSorting}
          />
        </div>
      </div>
    </div>
  );
}

export default ViewCauseList;
